import { useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  /** 削り終わったときに見せる文言（＝サーバーが決めた抽選結果） */
  label: string;
  running: boolean;
  onFinished: () => void;
}

/** 削り終わったと判定する割合 */
const REVEAL_THRESHOLD = 0.5;
/** 面積の読み出し間隔（ミリ秒）。指を動かすたびに全画素を読むと端末が重くなる */
const MEASURE_INTERVAL_MS = 120;

/**
 * スクラッチ演出。
 * 指でこすった面積が一定を超えたら結果を開示する。
 * 当選結果はサーバーが決めるため、この演出では抽選しない。
 */
export function Scratch({ label, running, onFinished }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const finishedRef = useRef(false);
  const measuredAtRef = useRef(0);
  const [revealed, setRevealed] = useState(false);

  // 銀色の被膜を描く。抽選結果が返るまで canvas は描画されないため、running を待って描く
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!running || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, '#8f8a93');
    gradient.addColorStop(0.5, '#cfc9d4');
    gradient.addColorStop(1, '#8f8a93');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.font = '600 16px sans-serif';
    ctx.fillStyle = '#5c5762';
    ctx.textAlign = 'center';
    ctx.fillText('指でこすってください', rect.width / 2, rect.height / 2);
  }, [running]);

  const measure = useCallback((): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 0;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let cleared = 0;
    // 4 ピクセルおきにサンプリングして負荷を抑える
    for (let i = 3; i < data.length; i += 16) {
      if ((data[i] ?? 0) === 0) cleared += 1;
    }
    return cleared / (data.length / 16);
  }, []);

  const reveal = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setRevealed(true);
    onFinished();
  }, [onFinished]);

  const scratchAt = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas || finishedRef.current) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(clientX - rect.left, clientY - rect.top, 22, 0, Math.PI * 2);
      ctx.fill();

      // 同じ場所を往復しても判定が進まないよう、実面積を見る。読み出しは間隔を空ける
      const now = Date.now();
      if (now - measuredAtRef.current < MEASURE_INTERVAL_MS) return;
      measuredAtRef.current = now;
      if (measure() > REVEAL_THRESHOLD) reveal();
    },
    [measure, reveal],
  );

  if (!running) {
    return (
      <div className="scratch">
        <div className="scratch__under">準備中…</div>
      </div>
    );
  }

  return (
    <div className="scratch">
      <div className="scratch__under">
        <span>{revealed ? label : ''}</span>
      </div>
      <canvas
        ref={canvasRef}
        className="scratch__canvas"
        style={{ opacity: revealed ? 0 : 1, transition: 'opacity 0.4s ease' }}
        onPointerDown={(e) => {
          drawingRef.current = true;
          scratchAt(e.clientX, e.clientY);
        }}
        onPointerMove={(e) => {
          if (drawingRef.current) scratchAt(e.clientX, e.clientY);
        }}
        onPointerUp={() => {
          drawingRef.current = false;
        }}
        onPointerLeave={() => {
          drawingRef.current = false;
        }}
      />
      {/* 指でこすれない端末・操作が難しい場合の代替。結果はサーバーが決めているので開示するだけ */}
      {!revealed && (
        <button type="button" className="btn btn--ghost scratch__reveal" onClick={reveal}>
          こすらずに開ける
        </button>
      )}
    </div>
  );
}
